import {
	JobStatusSchema,
	type JobWithRelations,
	ShortStatusSchema,
	type ShortWithRelations,
	VideoStatusSchema,
	type VideoWithRelations,
} from "@repo/types";
import mime from "mime";

export const defaultShortGeneratorFont = "gunters";

/**
 * Calculate progress percentage from video status
 * @param jobStatus - The job status
 * @param videoStatus - The video processing status
 * @returns Progress percentage (0-100)
 */
export function calculateProgressFromVideoStatus(
	jobStatus: string | null | undefined,
	videoStatus: string | null | undefined,
): number {
	// If job is completed, progress is 100%
	if (jobStatus === JobStatusSchema.Values.COMPLETED) {
		return 100;
	}

	// If job failed, progress is 0%
	if (jobStatus === JobStatusSchema.Values.FAILED) {
		return 0;
	}

	// Calculate progress based on video status stages
	if (videoStatus) {
		switch (videoStatus) {
			case VideoStatusSchema.Values.INITIATING_SERVICES:
				return 5;
			case VideoStatusSchema.Values.DOWNLOADING_VIDEO:
				return 15;
			case VideoStatusSchema.Values.TRANSCRIBING:
				return 30;
			case VideoStatusSchema.Values.GENERTING_SHORTS:
				return 60;
			case VideoStatusSchema.Values.GETTING_ASD:
				return 80;
			case VideoStatusSchema.Values.SUCCESS:
				return 100;
			case VideoStatusSchema.Values.FAILED:
				return 0;
			default:
				// Unknown status, use job status as fallback
				if (jobStatus === JobStatusSchema.Values.PROCESSING) {
					return 10; // Default progress for processing without video status
				}
				return 0;
		}
	}

	// Fallback to job status if no video status
	if (jobStatus === JobStatusSchema.Values.PROCESSING) {
		return 10;
	}

	return 0;
}

export function extractNumbersFromString(inputString: string): number | null {
	// Find all sequences of digits in the input string
	const digitMatches = inputString.match(/\d+/g);

	if (!digitMatches || digitMatches.length === 0) {
		// If no matches were found or the array is empty, return null
		return null;
	}

	// Get the last matched sequence of digits
	const lastDigitSequence = digitMatches[digitMatches.length - 1];

	// Convert the last matched sequence from string to number
	const lastNumber = Number.parseInt(lastDigitSequence, 10);

	return lastNumber;
}

export function getMimeType(filePath: string): string {
	const lastDotIndex = filePath.lastIndexOf(".");
	let extension = "";

	if (lastDotIndex !== -1) {
		extension = filePath.slice(lastDotIndex + 1);
	}
	return mime.getType(extension) || "application/octet-stream";
}

export function getExtensionFromUrl(url: string) {
	const parsedUrl = new URL(url);
	const path = parsedUrl.pathname;
	return getExtensionFromString(path);
}

export function getExtensionFromString(url: string) {
	const lastDotIndex = url.lastIndexOf(".");
	return lastDotIndex !== -1 ? url.slice(lastDotIndex + 1) : "";
}

export const getJsonFromLLMResponse = <T>(
	text: string,
	arrayStart: boolean,
): T => {
	let parsed: T;
	const startChar = arrayStart ? "[" : "{";
	const endChar = arrayStart ? "]" : "}";
	try {
		const responseArticle: string = text.substring(
			text.indexOf(startChar),
			text.lastIndexOf(endChar) + 1,
		);
		parsed = JSON.parse(responseArticle) as T;
		return parsed;
	} catch (error) {
		const preview =
			text.length > 800 ? `${text.slice(0, 800)}…[truncated ${text.length} chars]` : text;
		console.error(getJsonFromLLMResponse.name, error, preview);
		throw error;
	}
};

/**
 * map data to match the expected return
 * @param job
 * @returns
 */
export function mapVideoShortJob(job: Partial<JobWithRelations>) {
	const cleanedJob = JSON.parse(
		JSON.stringify(job),
	) as Partial<JobWithRelations> & { data: Partial<VideoWithRelations> };

	if (JobStatusSchema.Values.COMPLETED !== job.status) {
		// Calculate progress from video status (without exposing video status)
		const videoStatus = (job.video as VideoWithRelations | undefined)?.status;
		const progress = calculateProgressFromVideoStatus(job.status, videoStatus);
		return {
			jobId: job.id,
			status: job.status,
			createdAt: job.createdAt,
			progress: progress,
		};
	}
	cleanedJob.organization = undefined;
	cleanedJob.organizationId = undefined;
	cleanedJob.userId = undefined;
	cleanedJob.user = undefined;
	cleanedJob.webhook = undefined;
	cleanedJob.keyId = undefined;
	cleanedJob.key = undefined;
	const videoIdForClient = (job.video as VideoWithRelations | undefined)?.id;
	cleanedJob.data = JSON.parse(JSON.stringify(cleanedJob.video)) ?? {};
	cleanedJob.video = undefined;
	cleanedJob.data.job = undefined;
	if (cleanedJob.data) {
		cleanedJob.data.id = undefined;
		cleanedJob.data.jobId = undefined;
		cleanedJob.data.status = undefined;
		cleanedJob.data.createdAt = undefined;
	}
	cleanedJob.data.shorts = ((cleanedJob.data as VideoWithRelations)?.shorts
		.filter((s: ShortWithRelations) => s.status !== ShortStatusSchema.Values.FAILED)
		.map((s: ShortWithRelations) => {
			s.cropFilekey = null;
			return s
		})) as ShortWithRelations[] || [];
	(cleanedJob as any).progress = 100;
	(cleanedJob as any).videoId = videoIdForClient;
	return cleanedJob;
}

/**
 * map data to match the expected return for job list (without full nested video objects)
 * @param job
 * @returns
 */
export function mapJobList(job: Partial<JobWithRelations>) {
	const cleanedJob = JSON.parse(
		JSON.stringify(job),
	) as Partial<JobWithRelations>;

	// Remove sensitive/unnecessary fields
	cleanedJob.organization = undefined;
	cleanedJob.organizationId = undefined;
	cleanedJob.userId = undefined;
	cleanedJob.user = undefined;
	cleanedJob.webhook = undefined;
	cleanedJob.keyId = undefined;
	cleanedJob.key = undefined;
	cleanedJob.files = undefined;
	cleanedJob.renderHistories = undefined;
	cleanedJob.purchaseId = undefined;
	// If video exists, extract shorts count and add it directly to the job
	if (cleanedJob.video) {
		const videoWithRelations = cleanedJob.video as VideoWithRelations;
		const shortsCount = videoWithRelations.shorts
			.filter((short: ShortWithRelations) => short.status !== ShortStatusSchema.Values.FAILED)
			.length || 0;
		// Add shortsCount directly to the job
		(cleanedJob as any).shortsCount = shortsCount;
		// Extract necessary video fields for source display
		(cleanedJob as any).fileSource = videoWithRelations.sourceId ? 'youtube' : 'upload';
		// Remove the video object entirely since we only need the count and source info
		cleanedJob.video = undefined;
	}

	return cleanedJob;
}

/**
 * Map job data for API requests (RapidAPI format)
 * Returns simplified job data with shorts count or full shorts array depending on includeFullShorts parameter
 * @param job - The job with relations
 * @param includeFullShorts - If true, includes full shorts data in data.shorts array when job is completed. If false, only includes shortsCount.
 * @returns Simplified job data for API responses
 */
export function mapJobForApi(job: Partial<JobWithRelations>, includeFullShorts = false) {
	const cleanedJob = JSON.parse(
		JSON.stringify(job),
	) as Partial<JobWithRelations>;

	// Calculate progress from video status (without exposing video status)
	const videoStatus = (cleanedJob.video as VideoWithRelations | undefined)?.status;
	const progress = calculateProgressFromVideoStatus(cleanedJob.status, videoStatus);

	// Base response structure
	const response: {
		id: string;
		title: string | null;
		cost: number | null;
		costBreakDown: any;
		status: string | null;
		source: string | null;
		progress: number;
		shortsCount?: number;
		data?: {
			shorts: Array<{
				id: string;
				content: string;
				text: string;
				startTime: number;
				endTime: number;
				score?: {
					final_creative_score: number;
					narrative_payoff_score: number;
					hook_quality_score: number;
					engagement_potential_score: number;
					brand_alignment_score: number;
				};
			}>;
		};
	} = {
		id: cleanedJob.id || "",
		title: cleanedJob.title || null,
		cost: cleanedJob.cost || null,
		costBreakDown: cleanedJob.costBreakDown || null,
		status: cleanedJob.status || null,
		source: cleanedJob.source || null,
		progress: progress,
	};

	// Handle video and shorts data
	if (cleanedJob.video) {
		const videoWithRelations = cleanedJob.video as VideoWithRelations;
		if (videoWithRelations.shorts && Array.isArray(videoWithRelations.shorts)) {
			const validShorts = videoWithRelations.shorts
				.filter((short: ShortWithRelations) => short.status !== ShortStatusSchema.Values.FAILED);

			const shortsCount = validShorts.length || 0;
			response.shortsCount = shortsCount;

			// If includeFullShorts is true and job is completed, include full shorts data
			if (includeFullShorts && cleanedJob.status === JobStatusSchema.Values.COMPLETED && validShorts.length > 0) {
				response.data = {
					shorts: validShorts.map((short: ShortWithRelations) => {
						const shortWithScore = short as ShortWithRelations & { score?: {
							final_creative_score: number;
							narrative_payoff_score: number;
							hook_quality_score: number;
							engagement_potential_score: number;
							brand_alignment_score: number;
						} };
						return {
							id: String(short.id),
							content: short.title || "",
							text: short.text || "",
							startTime: short.startTime,
							endTime: short.endTime,
							...(shortWithScore.score && {
								score: {
									final_creative_score: shortWithScore.score.final_creative_score || 0,
									narrative_payoff_score: shortWithScore.score.narrative_payoff_score || 0,
									hook_quality_score: shortWithScore.score.hook_quality_score || 0,
									engagement_potential_score: shortWithScore.score.engagement_potential_score || 0,
									brand_alignment_score: shortWithScore.score.brand_alignment_score || 0,
								},
							}),
						};
					}),
				};
			}
		} else {
			response.shortsCount = 0;
		}
	}

	return response;
}